// Boundary module: persistent CRUD for user-managed triage rules, stored in
// `roamingSettings` under the `triageRules` key as a single JSON array.
// See docs/superpowers/plans/2026-07-02-gap-4-triage-auto-categorization.md.

/** The action a rule proposes when it matches an email. At least one of
 * `folder`/`categories` should be set, but this is not enforced at the type
 * level — `isRuleShape` only validates structural shape, not semantics. */
export interface RuleAction {
  folder?: string;
  categories?: string[];
}

/** A single natural-language assignment rule created/edited by the user. */
export interface Rule {
  id: string;
  name: string;
  /** Natural-language condition, evaluated semantically by the LLM against an email. */
  when: string;
  action: RuleAction;
}

const RULES_KEY = "triageRules";

// roamingSettings has a hard 32 KB ceiling shared with other keys (settings,
// styleProfile). Per-key budgets don't compose automatically: this key's
// ceiling must leave real headroom for the style profile (<= 2 KB) and the
// base settings (baseUrl/apiKey/model/graphClientId, plus Office's own
// serialization overhead) so the combined footprint doesn't breach 32 KB.
export const MAX_RULES_BYTES = 20 * 1024;

function isRuleActionShape(value: unknown): value is RuleAction {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  if (candidate.folder !== undefined && typeof candidate.folder !== "string") {
    return false;
  }
  if (
    candidate.categories !== undefined &&
    (!Array.isArray(candidate.categories) ||
      !candidate.categories.every((c) => typeof c === "string"))
  ) {
    return false;
  }
  return true;
}

function isRuleShape(value: unknown): value is Rule {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.when === "string" &&
    isRuleActionShape(candidate.action)
  );
}

/**
 * Loads the user's triage rules from `roamingSettings`. Degrades cleanly to an
 * empty list — never throws — when the key is absent, not valid JSON, not an
 * array, or contains entries that don't match the `Rule` shape (a corrupt or
 * hand-edited value should not crash the add-in).
 */
export function loadRules(): Rule[] {
  const raw = Office.context.roamingSettings.get(RULES_KEY) as string | undefined;
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isRuleShape);
  } catch {
    return [];
  }
}

/**
 * Persists the full rule list to `roamingSettings`. Throws if the serialized
 * list exceeds {@link MAX_RULES_BYTES} — callers should keep rules few/compact
 * (per the plan) rather than silently truncating user data.
 */
export async function saveRules(rules: Rule[]): Promise<void> {
  const json = JSON.stringify(rules);
  // `.length` counts UTF-16 code units, not bytes — multi-byte rule text
  // (non-ASCII names/conditions) would understate the real cost against
  // roamingSettings' byte-based ceiling. Measure the actual UTF-8 byte size.
  const byteLength = new TextEncoder().encode(json).length;
  if (byteLength > MAX_RULES_BYTES) {
    throw new Error(
      `Triage rules exceed the ${MAX_RULES_BYTES}-byte limit (${byteLength} bytes). ` +
        "Remove or shorten some rules."
    );
  }
  const rs = Office.context.roamingSettings;
  rs.set(RULES_KEY, json);
  return new Promise((resolve, reject) => {
    rs.saveAsync((result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(
          new Error(`Failed to save triage rules: ${result.error?.message ?? "unknown error"}`)
        );
      }
    });
  });
}

/** Generates a new rule ID. Not a security-sensitive identifier — collision
 * avoidance only, so `crypto.randomUUID` (with a fallback) is sufficient. */
export function generateRuleId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rule-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Creates a new rule or replaces an existing one by `id`, then persists the
 * full list. Returns the resulting list so callers (UI, agent tools) can
 * refresh their view without a separate `loadRules()` round-trip.
 */
export async function upsertRule(rule: Rule): Promise<Rule[]> {
  const existing = loadRules();
  const index = existing.findIndex((r) => r.id === rule.id);
  const next = index >= 0 ? existing.map((r, i) => (i === index ? rule : r)) : [...existing, rule];
  await saveRules(next);
  return next;
}

/** Deletes a rule by `id` (a no-op if the ID isn't found), then persists the list. */
export async function deleteRule(id: string): Promise<Rule[]> {
  const next = loadRules().filter((r) => r.id !== id);
  await saveRules(next);
  return next;
}
